import { exerciseTypes } from 'z-js-commons';
import { get } from 'lodash';

import { COUNT_ALPHABET_CHARS, START_ALPHABET_CHAR_CODE } from '../constants/constants';
import { isCheckerCorrectable, getCheckerCorrection } from './checker';

export const CORRECT = 'CORRECT';
export const WRONG = 'WRONG';
export const MISSING = 'MISSING';
export const INCOMPLETE = 'INCOMPLETE';
export const WAITING_FOR_CORRECTION = 'WAITING_FOR_CORRECTION';

export function getCharByElementPosition(indexPosition) {
    indexPosition--;
    let firstLetter = Math.floor(indexPosition / COUNT_ALPHABET_CHARS);
    let secondLetter = indexPosition % COUNT_ALPHABET_CHARS;
    return `${firstLetter !== 0 ? String.fromCharCode(START_ALPHABET_CHAR_CODE + firstLetter) : ''}${String.fromCharCode(
        START_ALPHABET_CHAR_CODE + secondLetter + 1,
    )}`;
}

export function parseElement(question, options) {
    const searchIdRegex = new RegExp(/<gap_ref[^>]+idref\s*=\s*['"]([^'"]+)['"][^>]*>/g);
    const gapsMap = new Map();
    let match = null;
    let count = 0;
    while ((match = searchIdRegex.exec(question))) {
        const elementId = match[1].substring(1);
        const element = options.find((option) => option.id === elementId);
        count++;
        gapsMap.set(match[1], {
            responseId: elementId,
            text: element && element.values[0].text,
            char: getCharByElementPosition(count),
        });
    }

    return gapsMap;
}

export function orderOptions(question, options) {
    const searchIdRegex = new RegExp(/<gap_ref[^>]+idref\s*=\s*['"]([^'"]+)['"][^>]*>/g);
    const retOptions = [];
    let match = null;
    while ((match = searchIdRegex.exec(question))) {
        const elementId = match[1].substring(1);
        const option = options.find((option) => option.id === elementId);
        retOptions.push(option);
    }
    return retOptions;
}

export function parseTextareaAnswer(answer = '') {
    return answer.trim().replace(/['´’′‵‘`]/g, "'");
}

export function deleteHtmlTagsFromString(text) {
    return text.replace(/<(.|\n)*?>/g, '');
}

export const scrollToTop = () => {
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
};

export function getExerciseTypeFromName(typeLabel) {
    let exerciseType = exerciseTypes.getExerciseTypeFromName(typeLabel);
    return exerciseType === exerciseTypes.SHOTS ? 'Domande in serie' : exerciseType;
}

export const getExercisePrompt = (exercise, singleAnswer) => {
    switch (exercise.typeLabel) {
        case 'exercise.multiplechoice': {
            const correctAnswers = exercise.options.filter((x) => x.correct);
            return correctAnswers.length === 1
                ? 'scegli l’unica risposta esatta tra quelle proposte'
                : 'scegli tutte le risposte esatte tra quelle proposte.';
        }
        case 'exercise.opengap':
            return "completa l'esercizio con le risposte che ti sembrano esatte.";
        case 'exercise.closedgap':
            return 'scegli la risposta esatta per ogni gruppo di opzioni proposte.';
        case 'exercise.finderror':
            return 'seleziona le parti di testo che ritieni sbagliate. Controlla il contatore per verificare di aver selezionato il numero giusto di errori.';
        case 'exercise.dragndrop':
            return 'completa le parti mancanti dell’esercizio con le alternative proposte.';
        case 'exercise.truefalse':
            return 'vero o falso? Scegli la risposta esatta.';
        case 'exercise.openanswer':
            return 'scrivi nel riquadro la tua risposta alla domanda. La correzione la farà l’insegnante.';
        case 'exercise.barrage':
            return 'questo esercizio consiste in una serie di domande. Per ciascuna, scegli l’unica risposta esatta.';
        case 'exercise.checker':
            switch (singleAnswer) {
                case true:
                    return 'scrivi il testo del passaggio e vai avanti: scopri se hai sbagliato e correggi subito. Quando pensi di aver finito scegli la risposta che ti sembra esatta.';
                default:
                    return 'scrivi il testo del passaggio e vai avanti. Quando pensi di aver finito scegli la risposta che ti sembra esatta.';
            }
        default:
            return 'Tipologia di esercizio non implementata.';
    }
};

export const checkExerciseCorrection = async (exercise, answers) => {
    const exerciseType = exerciseTypes.getExerciseTypeFromName(exercise.typeLabel);

    if (answers.length === 0) {
        return MISSING;
    }

    switch (exerciseType) {
        case exerciseTypes.MULTIPLE_CHOICE: {
            const correctIds = exercise.options
                .filter((item) => item.correct === true)
                .map((item) => item.id)
                .sort((a, b) => a - b);
            const newAnswers = answers.map((item) => parseInt(item)).sort((a, b) => a - b);
            return JSON.stringify(correctIds) === JSON.stringify(newAnswers) ? CORRECT : WRONG;
        }
        case exerciseTypes.TRUE_FALSE:
            return exercise.questions.reduce((isExerciseCorrect, question) => {
                const answer = answers.find((item) => item.questionId === `${question.id}`);
                if (!answer) {
                    return false;
                }
                if (question.correct) {
                    return answer.value && isExerciseCorrect;
                }
                return !answer.value && isExerciseCorrect;
            }, true)
                ? CORRECT
                : WRONG;

        case exerciseTypes.FILL_CHOICE:
            if (answers.length !== exercise.options.length) {
                return WRONG;
            }
            return answers.every((currentAnswer) => {
                const options = exercise.options.find((option) => option.id === currentAnswer.gapId);

                return options.values.find((value) => value.correct && `${value.id}` === currentAnswer.optionId);
            })
                ? CORRECT
                : WRONG;

        case exerciseTypes.SHOTS:
            return exercise.shots.reduce((isAnswerCorrect, shot) => {
                const myAnswer = answers.find((answer) => answer.shotId === shot.id);
                if (!myAnswer) {
                    return false;
                }
                return shot.idRef === myAnswer.answerId && isAnswerCorrect;
            }, true)
                ? CORRECT
                : WRONG;

        case exerciseTypes.DRAG_DROP: {
            const gapList = Array.from(parseElement(exercise.question, exercise.options));
            const parsedGapList = gapList.map((item) => {
                return {
                    id: item[0].replace('_', ''),
                    correctAnswerId: item[1].responseId,
                    text: item[1].text,
                };
            });
            let isCorrect = true;
            parsedGapList.forEach((item) => {
                let selectedAnswer = get(
                    answers.find((answer) => answer.gapId === item.id),
                    'optionId',
                );
                const selectedText = get(
                    exercise.options.find((item) => item.id === selectedAnswer),
                    'values[0].text',
                );

                if (selectedAnswer === undefined || selectedText !== item.text) {
                    isCorrect = false;
                }
            });
            return isCorrect ? CORRECT : WRONG;
        }
        case exerciseTypes.FILL_BLANKS:
            return exercise.options.reduce((isCorrect, option) => {
                const answer = answers.find((item) => item.gapId === option.id);
                const parsedCorrectValues = option.values.map((value) => deleteHtmlTagsFromString(value));
                if (!answer) {
                    return false;
                }
                const parsedAnswer = deleteHtmlTagsFromString(answer.value);
                return (
                    parsedCorrectValues.some((correctValue) => parseTextareaAnswer(parsedAnswer) === parseTextareaAnswer(correctValue)) &&
                    isCorrect
                );
            }, true)
                ? CORRECT
                : WRONG;

        case exerciseTypes.FIND_ERROR: {
            const correctAnswers = exercise.wrongWords;

            if (correctAnswers.length !== answers.length) {
                return WRONG;
            }

            return correctAnswers.every((correctAnswer) =>
                answers.find((answer) => correctAnswer.start === answer.start && correctAnswer.end === answer.end),
            )
                ? CORRECT
                : WRONG;
        }
        case exerciseTypes.CHECKER:
            return getCheckerCorrection(exercise, answers);

        case exerciseTypes.OPEN_ANSWER:
            return WAITING_FOR_CORRECTION;
        default:
            return CORRECT;
    }
};

export const isCorrectionEnabled = (exercise = {}, answers, correctionsStatus) => {
    const exerciseType = exerciseTypes.getExerciseTypeFromName(exercise.typeLabel);

    const hasAnswers = answers.some((answer) => answer.exerciseId === exercise.id && answer.answer.length > 0);
    const hasCorrection = correctionsStatus.some((correctionStatus) => correctionStatus.exerciseId === exercise.id);

    switch (exerciseType) {
        case exerciseTypes.CHECKER: {
            const { answer = [] } = answers.find((answer) => answer.exerciseId === exercise.id) || {};

            return !hasCorrection && isCheckerCorrectable(answer);
        }
        default:
            return hasAnswers && !hasCorrection;
    }
};

export const getCorrectAnswers = (exercise) => {
    const exerciseType = exerciseTypes.getExerciseTypeFromName(exercise.typeLabel);

    switch (exerciseType) {
        case exerciseTypes.SHOTS:
            return {
                exerciseId: exercise.id,
                answer: exercise.shots.map((item) => {
                    return { shotId: item.id, answerId: item.idRef };
                }),
            };
        case exerciseTypes.MULTIPLE_CHOICE:
            return {
                exerciseId: exercise.id,
                answer: exercise.options.filter((item) => item.correct === true).map((item) => item.id),
            };
        case exerciseTypes.FILL_BLANKS:
            return {
                exerciseId: exercise.id,
                answer: exercise.options.map((item) => {
                    return {
                        gapId: item.id,
                        value: item.values[0],
                    };
                }),
            };
        case exerciseTypes.FILL_CHOICE:
            return {
                exerciseId: exercise.id,
                answer: exercise.options.map((item) => {
                    return {
                        gapId: item.id,
                        optionId: `${get(
                            item.values.find((option) => option.correct === true),
                            'id',
                        )}`,
                    };
                }),
            };
        case exerciseTypes.TRUE_FALSE:
            return {
                exerciseId: exercise.id,
                answer: exercise.questions.map((question) => {
                    const correctOptions = question.options.find((option) => option.correct === true);
                    return {
                        questionId: `${question.id}`,
                        value: correctOptions.id,
                    };
                }),
            };
        case exerciseTypes.FIND_ERROR:
            return {
                exerciseId: exercise.id,
                answer: exercise.wrongWords.map((wrongWord) => {
                    return {
                        start: wrongWord.start,
                        end: wrongWord.end,
                    };
                }),
            };
        default:
            return {};
    }
};
